import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { CartItem, CartState } from "./cartTypes";

type CartContextValue = {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (item: CartItem) => void;
  updateQuantity: (item: CartItem, quantity: number) => void;
  clear: () => void;
};

const STORAGE_KEY = "sibella_cart_v2";

const CartContext = createContext<CartContextValue | undefined>(undefined);

function loadCart(): CartState {
  if (typeof window === "undefined") {
    return { items: [] };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw) as CartState;
    if (!parsed?.items) return { items: [] };
    return parsed;
  } catch {
    return { items: [] };
  }
}

function saveCart(state: CartState) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CartState>({ items: [] });

  useEffect(() => {
    setState(loadCart());
  }, []);

  useEffect(() => {
    saveCart(state);
  }, [state]);

  const addItem = useCallback((item: CartItem) => {
    setState((prev) => {
      const existing = prev.items.find(
        (entry) =>
          entry.productId === item.productId && entry.size === item.size
      );

      if (existing) {
        return {
          items: prev.items.map((entry) =>
            entry === existing
              ? { ...entry, quantity: entry.quantity + item.quantity }
              : entry
          ),
        };
      }

      return { items: [...prev.items, item] };
    });
  }, []);

  const removeItem = useCallback((item: CartItem) => {
    setState((prev) => ({
      items: prev.items.filter(
        (entry) =>
          entry.productId !== item.productId || entry.size !== item.size
      ),
    }));
  }, []);

  const updateQuantity = useCallback((item: CartItem, quantity: number) => {
    setState((prev) => ({
      items: prev.items
        .map((entry) =>
          entry.productId === item.productId && entry.size === item.size
            ? { ...entry, quantity }
            : entry
        )
        .filter((entry) => entry.quantity > 0),
    }));
  }, []);

  const clear = useCallback(() => {
    setState({ items: [] });
  }, []);

  const value = useMemo(
    () => ({
      items: state.items,
      addItem,
      removeItem,
      updateQuantity,
      clear,
    }),
    [state.items, addItem, removeItem, updateQuantity, clear]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
}
